import { LogUserActivity } from '@/apis/users/requests'
import {
  AlertOptions,
  AlertResult,
  useAppAlert,
} from '@/app/AppAlertProvider.tsx'
import DocumentPreviewNew from '@/app/common/components/Documents/DocumentPreviewNew'
import { SESSION_STORAGE_KEYS } from '@/app/common/session-storage'
import { SubmitIncome } from '@/app/pages/family_application/student_details/hooks/usePostDocumentVerifyIncome.tsx'
import { global_error_codes } from '@/common/constants.ts'
import { RequestError } from '@/common/types_and_interfaces.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import {
  resetOCRAttempts,
  selectFamilyApplication,
  setApplicationDocumentSubmitted,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice.ts'
import { AppDispatch, RootState } from '@/storage/store'
import { MutationStatus } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../../constants'
import useTaxableIncomeDocumentParams from '../hooks/useTaxableIncomeDocumentParams'
import useUploadParams from '../hooks/useUploadParams'

type Props = {
  image: File
}

const UserErrors = [
  global_error_codes.file_size_limit_exceeded,
  global_error_codes.file_type_not_allowed,
]

export const DocumentReviewPage = ({ image }: Props) => {
  const [status, setStatus] = useState<MutationStatus>('idle')
  const programId = useSelector(selectProgramID)
  const { t: translate } = useTranslation()
  const { onRouteChange, info } = useTaxableIncomeDocumentParams()
  const navigateTo = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()
  const { selectedMember } = useUploadParams()

  const selectedForm = info()

  const documentTypeMap: Record<string, string> = {
    form_1040: 'TAX_INCOME_1040',
    form_w2: 'TAX_INCOME_W2',
    form_1099: 'TAX_INCOME_1099',
    social_security: 'SOCIAL_SECURITY',
    non_taxable: 'NON_TAXABLE',
    disability_benefits: 'DISABILITY_BENEFITS',
    child_support_payments: 'CHILD_SUPPORT_PAYMENTS',
    alimony_payments: 'ALIMONY_PAYMENTS',
    other: 'OTHER',
  }

  const documentType = documentTypeMap[selectedForm.code] || ''

  const { selectedStudentId } = useSelector(
    (state: RootState) => state.FamilyApplication
  )
  const familyApplication = useSelector(selectFamilyApplication)

  const handleSubmitError = (error: RequestError) => {
    setStatus('error')
    let alertConfig: AlertOptions
    let callback: (result: AlertResult) => void
    if (
      error.code == global_error_codes.external_service_error ||
      (UserErrors as number[]).includes(error.code)
    ) {
      alertConfig = {
        variant: 'error',
        message: error.message,
        confirmBtnText: translate('retry'),
      }
      callback = () => {
        navigateTo(-1)
        return
      }
    } else {
      alertConfig = {
        variant: 'error',
        titleText: error.message ?? translate('error_processing'),
        message: translate('support_review_docs'),
        confirmBtnText: translate('submit'),
        denyBtn: true,
        denyBtnText: translate('try_again'),
      }

      callback = ({ denied, confirmed }) => {
        if (denied) {
          navigateTo(-1)
          return
        }
        if (confirmed) {
          onRouteChange(
            AppRoutePaths.family_application.income_verification
              .document_submitted
          )
          return
        }
      }
    }

    fireAlert(alertConfig).then(callback)
  }

  const handleSubmitSuccess = () => {
    if (selectedForm && selectedMember)
      dispatch(
        setApplicationDocumentSubmitted({
          form: selectedForm.code,
          member: selectedMember,
        })
      )

    const formName =
      selectedForm.code == 'social_security'
        ? 'Social Security Benefits'
        : translate(selectedForm.code, { lng: 'en' })

    LogUserActivity({
      log_type: LOG_TYPES.REGISTER,
      suffix_text: `non file taxes for ${formName}.`,
    })

    setStatus('success')
    fireAlert({
      variant: 'success',
      titleText: translate('document_identified'),
      denyBtn: true,
      denyBtnText: 'Retry',
      confirmBtnText: 'Confirm',
    }).then(({ confirmed, denied }) => {
      const documentSubmittedPath =
        AppRoutePaths.family_application.income_verification.document_submitted
      if (confirmed || denied) {
        onRouteChange(documentSubmittedPath)
      }
    })
  }

  const onSubmit = async () => {
    setStatus('pending')
    const form = new FormData()
    form.append('file', image)
    form.append('document_type', documentType)
    form.append('student_id', String(selectedStudentId))
    form.append('program_id', String(programId))
    form.append('application_id', String(familyApplication.application_id))
    if (selectedMember === 'partner') {
      form.append('document_owner', 'spouse')
    }
    const { data: responseData, error } = await SubmitIncome(form)

    if (selectedMember === 'partner') {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.hasUploadedSpouseIncome,
        'true'
      )
    } else {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.hasUploadedApplicantIncome,
        'true'
      )
    }

    if (responseData) {
      dispatch(resetOCRAttempts())
      handleSubmitSuccess()
    } else {
      handleSubmitError(error)
    }
  }

  return (
    <section
      id="income-verification"
      aria-labelledby="income-verification-title"
    >
      <h1
        id="income-verification-title"
        className="text-3xl text-center font-medium text-gray-900 mb-10 sm:mb-5"
      >
        {translate('photo_review')}
      </h1>

      <div className="flex justify-center">
        <DocumentPreviewNew
          file={image}
          status={status}
          onContinueClick={onSubmit}
          onRetakeClick={() => {
            navigateTo(-1)
          }}
        />
      </div>
    </section>
  )
}
